import { Log, SigninRequest, User, UserManager, UserManagerSettings } from "oidc-client";

Log.logger = console;
Log.level = Log.INFO;

// singleton class
export default class AuthenticationService {

  public static getInstance(): AuthenticationService {
    return AuthenticationService.instance;
  }
  private static instance: AuthenticationService = new AuthenticationService();

  public userManager: UserManager;
  private user: User = null;

  private constructor() {
    const settings: UserManagerSettings = {
      authority: window.authEndpoint,
      automaticSilentRenew: true,
      client_id: "omnius.js",
      post_logout_redirect_uri: window.customUIAbsolutePath,
      redirect_uri: `${window.customUIAbsolutePath}/callback`,
      response_type: "code",
      revokeAccessTokenOnSignout: true,
      scope: "openid profile email role api1 IdentityServerApi QuestCustom qesi",
      silent_redirect_uri: `${window.customUIAbsolutePath}/silent-refresh-callback`,
    };
    this.userManager = new UserManager(settings);

    this.userManager.events.addAccessTokenExpired(async () => {
      console.warn("Access Token Expired, forcing signout");

      const token = this.user.id_token;

      await this.removeUser();
      await this.userManager.signoutRedirect({ id_token_hint: token });
    });

    this.userManager.events.addUserSignedOut(async () => {
      console.log("UserSignedOut callback invoked. Cleaning up and initiating login");
      await this.removeUser();
      await this.login();
    });
  }

  public getUser(): User | null {
    return this.user
      ? { ...this.user } as User
      : null;
  }

  public async loadUser(): Promise<User | null> {
    this.user = await this.userManager.getUser();
    return this.getUser();
  }

  public login(): Promise<void> {
    // save the current path in session so we can restore the path in authenticationCallback.tsx
    const currentPath = window.location.pathname + window.location.search;
    console.log("Saving post-login redirect url " + currentPath);
    sessionStorage.setItem("LoginRedirect", currentPath);
    return this.userManager.signinRedirect();
  }

  public async renewToken(): Promise<User> {
    this.user = await this.userManager.signinSilent();
    return this.getUser();
  }

  public async logout(logoutReason: string): Promise<void> {
    const user = this.user;
    await this.removeUser();
    return this.userManager.signoutRedirect({
      extraQueryParams: {
        logoutReason,
      },
      id_token_hint: user ? user.id_token : null,
    });
  }

  public async removeUser(): Promise<void> {
    this.user = null;
    await this.userManager.removeUser();
    await this.userManager.clearStaleState();
  }

  public async signInSilentCallback(): Promise<User | null> {
    this.user = await this.userManager.signinSilentCallback();
    return this.getUser();
  }

  public async signInRedirectCallback(): Promise<User | null> {
    this.user = await this.userManager.signinRedirectCallback();
    return this.getUser();
  }

  public createSignInRequest(): Promise<SigninRequest> {
    return this.userManager.createSigninRequest();
  }

  public isUserSignedIn(): boolean {
    return this.user && this.user.access_token && !this.user.expired;
  }
}
